import type { TFunction } from "i18next";
import { normalizeFoodIdentity } from "@household/shared/mealAssembler";
import { money } from "../../app/helpers.js";
import type { EntityReferenceId, Product } from "../../app/types.js";

// Price history (#595) at the surfaces where the buy decision actually happens
// (#697). The catalog payload already carries the derived numbers, so every
// consumer here is passive: no fetch, no extra tap, nothing to confirm.
//
// WORDING RULE: observations are per-package and are NOT normalized by unit
// size (deferred to #587), so a 12 oz jar and a 40 oz jar compare as packages.
// Nothing in this module may claim "cheapest" — only "lowest recorded" — and
// the per-package caveat must reach the eye, not just the hover (see
// typicalPriceCaveat).
//
// The copy is translated (#1088), and because this is a pure module with no
// hook, every consumer hands its own `t` in — the shape shelfLifeNote.ts and
// the floor-plan helpers already use. Two DIFFERENT namespaces render these
// sentences: the list sheet translates against `common`, the shopping view
// against `shopping`. So the keys live in `common` and every call names that
// namespace explicitly — a key resolved against the caller's own bundle would
// be found on one surface and painted as a raw key path on the other. `common`
// is one of the two eagerly initialised namespaces (app/i18n.ts), so it is
// always loaded by the time either caller renders and neither needs a gate.

/** Deliberately the un-parameterised `TFunction`: callers arrive holding a `t`
 * fixed to their own namespace, and each call below overrides it with
 * `{ ns: "common" }`. */
type Translate = TFunction;

export type TypicalPrice = {
  typicalPriceMinor: number;
  currency: string;
  /** Every observation on file in this currency — up to the API's 100-row cap. */
  observationCount: number;
  /** How many of those actually fed the median, or null when the payload did
   * not say (a response predating the window field). Never claim the median
   * covers `observationCount` unless the two are equal. */
  medianSampleSize: number | null;
  lowestStore: { storeName: string; typicalPriceMinor: number } | null;
};

/** A draft price within this fraction of typical reads as "about usual" rather
 * than over/under — cent-level noise is not a signal worth flagging. */
const TYPICAL_PRICE_TOLERANCE = 0.05;

/** The binding caveat, as VISIBLE copy. This ships to a phone-first surface
 * (#697), where a title attribute never renders, so any surface that leans on
 * the tooltip alone has to render this string too. */
export function typicalPriceCaveat(t: Translate): string {
  return t("typicalPrice.caveat", { ns: "common" });
}

export function typicalPriceOf(product: Product | null | undefined): TypicalPrice | null {
  if (!product || product.typicalPriceMinor == null || product.typicalPriceMinor <= 0) return null;
  const observationCount = product.typicalPriceObservationCount ?? 0;
  const medianWindow = product.typicalPriceWindow;
  return {
    typicalPriceMinor: product.typicalPriceMinor,
    currency: product.typicalPriceCurrency || "USD",
    observationCount,
    // The median is taken over the newest `typicalPriceWindow` observations, so
    // the sample is the smaller of the two — never the raw count.
    medianSampleSize: medianWindow && observationCount > 0 ? Math.min(observationCount, medianWindow) : null,
    lowestStore: product.lowestStore ?? null
  };
}

/**
 * Same-food match only, so a note can never quote a different food's price.
 *
 * Two passes, both identity-preserving: the trimmed name casefolded, then
 * normalizeFoodIdentity, which folds punctuation, spacing and plurals but
 * KEEPS descriptor words. It is deliberately not normalizeFoodName — that one
 * strips canned/fresh/whole/ground/organic/…, so "Fresh Tomatoes" and "Canned
 * Tomatoes" both collapse to "tomato" and the shopper would be shown the wrong
 * food's price with no way to tell. Anything that does not clearly land on one
 * catalog product gets no note at all; an ambiguous fold (two catalog foods
 * sharing an identity key) is treated as no match for the same reason.
 */
export function findProductByName(name: string, products: Product[]): Product | undefined {
  const trimmed = name.trim();
  if (!trimmed) return undefined;
  const lowered = trimmed.toLowerCase();
  // Both passes bail on ambiguity rather than taking the first hit: two catalog
  // rows sharing a name carry two separate price histories, and picking one of
  // them is a coin toss the shopper cannot see.
  const exact = products.filter((product) => product.name.trim().toLowerCase() === lowered);
  if (exact.length) return exact.length === 1 ? exact[0] : undefined;
  const identity = normalizeFoodIdentity(trimmed);
  if (!identity) return undefined;
  const folded = products.filter((product) => normalizeFoodIdentity(product.name) === identity);
  return folded.length === 1 ? folded[0] : undefined;
}

/** Resolve a row to its catalog price summary: an explicit product link wins,
 * otherwise the typed name has to name the same food as a catalog product. */
export function typicalPriceForRow(
  row: { productId?: EntityReferenceId | string | null; name?: string },
  products: Product[]
): TypicalPrice | null {
  const linked = row.productId ? products.find((product) => String(product.id) === String(row.productId)) : undefined;
  return typicalPriceOf(linked ?? (row.name ? findProductByName(row.name, products) : undefined));
}

/** One-line passive note: "usually ~$4.79 per package · lowest recorded $4.29
 * at Aldi". "per package" is in the visible copy on purpose. */
export function typicalPriceNote(typical: TypicalPrice, t: Translate): string {
  const parts = [t("typicalPrice.note", { ns: "common", price: money(typical.typicalPriceMinor, typical.currency) })];
  const store = typical.lowestStore;
  // Only worth naming a store when it is actually below typical — quoting the
  // one store you ever shopped at as "lowest" says nothing.
  if (store?.storeName && store.typicalPriceMinor < typical.typicalPriceMinor) {
    parts.push(t("typicalPrice.noteLowest", {
      ns: "common",
      price: money(store.typicalPriceMinor, typical.currency),
      store: store.storeName
    }));
  }
  return parts.join(" · ");
}

/** "4 recorded purchases" — its own key so the plural is the bundle's problem,
 * not a ternary that only knows English's two forms. */
function purchaseCount(count: number, t: Translate): string {
  return t("typicalPrice.recorded", { ns: "common", count });
}

/** Tooltip that spells out what the number is — and what it is not. The median
 * covers only the newest `medianSampleSize` observations, while
 * `observationCount` is everything on file, so the wording names the sample it
 * actually used and reports the rest separately rather than implying every
 * recorded purchase fed the number. */
export function typicalPriceTitle(typical: TypicalPrice, t: Translate): string {
  const sample = typical.medianSampleSize;
  const total = typical.observationCount;
  const basis = sample == null
    ? t("typicalPrice.basisRecent", { ns: "common" })
    : sample >= total
      // `count` here selects the FRAME's plural, not the number in it: Spanish
      // needs "su"/"sus" around the count that `recorded` already carries.
      ? t("typicalPrice.basisAll", { ns: "common", count: total, recorded: purchaseCount(total, t) })
      : t("typicalPrice.basisWindow", { ns: "common", sample, recorded: purchaseCount(total, t) });
  return t("typicalPrice.title", { ns: "common", basis, caveat: typicalPriceCaveat(t) });
}

/** How the price being typed right now sits against typical, or null when it is
 * close enough that saying anything would be noise.
 *
 * Currencies must match: the API keeps its medians inside a single report
 * currency on purpose, and subtracting a euro median from a dollar draft would
 * throw that away at the last step. Different currency, no comparison. */
export function draftPriceComparison(
  draftPriceMinor: number,
  draftCurrency: string,
  typical: TypicalPrice,
  t: Translate
): string | null {
  if (!(draftPriceMinor > 0)) return null;
  if ((draftCurrency || "").trim().toUpperCase() !== typical.currency.trim().toUpperCase()) return null;
  const difference = draftPriceMinor - typical.typicalPriceMinor;
  if (Math.abs(difference) <= Math.round(typical.typicalPriceMinor * TYPICAL_PRICE_TOLERANCE)) {
    return t("typicalPrice.aboutUsual", { ns: "common" });
  }
  // Over and under are separate keys rather than one sentence with a swapped
  // word: which side of the number the direction falls on is the translator's
  // call, not the template's.
  const amount = money(Math.abs(difference), typical.currency);
  return difference > 0
    ? t("typicalPrice.over", { ns: "common", amount })
    : t("typicalPrice.under", { ns: "common", amount });
}
